import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { DateRangePickerService, DateRange } from './date-range-picker.service';
import { Subscription } from 'rxjs';

interface Calendar {
  current: any[][];
  next: any[][];
}

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
  @Output() dateRangeSelected = new EventEmitter<{ start: Date; end: Date }>();

  calendar: Calendar = {
    current: [],
    next: []
  };

  selectedRange: DateRange = {
    start: new Date(),
    end: new Date(),
    type: 'mtd'
  };

  currentMonth: Date = new Date();
  nextMonth: Date = new Date();

  ranges = [
    { label: 'All', value: 'all' },
    //{ label: 'Last 7 Days', value: 'last7' },
    //{ label: 'Last 30 Days', value: 'last30' },
    { label: 'Last Year', value: 'lastYear' },
    { label: 'Last Month', value: 'lastMonth' },
    { label: 'Last Week', value: 'lastWeek' },
    { label: 'MTD', value: 'mtd' },
    // { label: 'YTD', value: 'ytd' },
    { label: 'Custom', value: 'custom' }
  ];

  selectedRangeType: string = 'mtd';
  isSelectingRange: boolean = false;
  hoveredDate: Date | null = null;
  weekDays = ['Sa', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  lastUpdateDate = new Date();
  private isInitialized = false;

  // Untuk mengelola subscription
  private subscription = new Subscription();

  constructor(private dateRangeService: DateRangePickerService) { }

  ngOnInit() {
    this.currentMonth = new Date();
    this.nextMonth = new Date(this.currentMonth);
    this.nextMonth.setMonth(this.nextMonth.getMonth() + 1);

    this.generateCalendars();

    // Cek apakah ada rentang tanggal yang tersimpan
    if (this.dateRangeService.hasStoredDateRange()) {
      // Jika ada, kita akan langsung menggunakan data tersebut
      const startDateStr = localStorage.getItem('startDate');
      const endDateStr = localStorage.getItem('endDate');
      const rangeType = localStorage.getItem('rangeType') || 'mtd';

      if (startDateStr && endDateStr) {
        this.selectedRange = {
          start: new Date(startDateStr),
          end: new Date(endDateStr),
          type: rangeType
        };
        this.selectedRangeType = rangeType;
      }
    } else {
      // Jika tidak ada, inisialisasi dengan rentang default
      const defaultRange = this.dateRangeService.getDateRange('mtd');
      this.selectedRange = defaultRange;
      this.selectedRangeType = 'mtd';

      // Simpan ke service
      this.dateRangeService.saveDateRange(
        defaultRange.start,
        defaultRange.end,
        'mtd'
      );
    }

    // Berlangganan pada observable dateRange$
    this.subscription.add(
      this.dateRangeService.dateRange$.subscribe(range => {
        // Perbarui hanya jika komponen sudah diinisialisasi
        // atau jika nilai berbeda dari yang sudah ada
        if (!this.isInitialized ||
            this.selectedRange.start.getTime() !== range.start.getTime() ||
            this.selectedRange.end.getTime() !== range.end.getTime() ||
            this.selectedRangeType !== range.type) {

          this.selectedRange = range;
          this.selectedRangeType = range.type || 'mtd';

          // Emit event saat ada perubahan pada range
          this.dateRangeSelected.emit({
            start: this.selectedRange.start,
            end: this.selectedRange.end
          });

          // Log untuk debug
          console.log('DateRangePicker updated from service:', {
            type: this.selectedRangeType,
            start: this.selectedRange.start.toLocaleDateString(),
            end: this.selectedRange.end.toLocaleDateString()
          });

          this.isInitialized = true;
        }
      })
    );

    // Tambahkan event listener untuk storage event
    window.addEventListener('storage', this.handleStorageChange.bind(this));
  }

  updateCalendarView() {
    // Jika tanggal mulai berada di bulan yang berbeda, perbarui tampilan kalender
    const startMonth = this.selectedRange.start.getMonth();
    const currentDisplayedMonth = this.currentMonth.getMonth();

    if (startMonth !== currentDisplayedMonth) {
      this.currentMonth = new Date(this.selectedRange.start);
      this.nextMonth = new Date(this.currentMonth);
      this.nextMonth.setMonth(this.nextMonth.getMonth() + 1);
      this.generateCalendars();
    }
  }

  ngOnDestroy() {
    // Bersihkan subscription saat komponen dihancurkan
    this.subscription.unsubscribe();

    // Hapus event listener
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
  }

  // Handler untuk storage event
  private handleStorageChange(event: StorageEvent) {
    if (event.key === 'startDate' || event.key === 'endDate' || event.key === 'rangeType') {
      const startDateStr = localStorage.getItem('startDate');
      const endDateStr = localStorage.getItem('endDate');
      const rangeType = localStorage.getItem('rangeType');

      if (startDateStr && endDateStr && rangeType) {
        const start = new Date(startDateStr);
        const end = new Date(endDateStr);

        this.selectedRange = { start, end, type: rangeType };
        this.selectedRangeType = rangeType;

        // Perbarui tampilan kalender
        this.updateCalendarView();

        // Emit event
        this.dateRangeSelected.emit({ start, end });
      }
    }
  }

  generateCalendars() {
    this.calendar['current'] = this.dateRangeService.generateCalendarMonth(this.currentMonth);
    this.calendar['next'] = this.dateRangeService.generateCalendarMonth(this.nextMonth);
  }

  navigateMonth(direction: 'prev' | 'next') {
    const newCurrentMonth = new Date(this.currentMonth);
    const newNextMonth = new Date(this.nextMonth);

    if (direction === 'prev') {
      newCurrentMonth.setMonth(newCurrentMonth.getMonth() - 1);
      newNextMonth.setMonth(newNextMonth.getMonth() - 1);
    } else {
      newCurrentMonth.setMonth(newCurrentMonth.getMonth() + 1);
      newNextMonth.setMonth(newNextMonth.getMonth() + 1);
    }

    // Handle year change
    if (newCurrentMonth.getMonth() === 11) {
      newNextMonth.setFullYear(newCurrentMonth.getFullYear() + 1);
    } else if (newCurrentMonth.getMonth() === 0) {
      newNextMonth.setFullYear(newCurrentMonth.getFullYear());
    }

    this.currentMonth = newCurrentMonth;
    this.nextMonth = newNextMonth;
    this.generateCalendars();
  }



  private refreshPage(): void {
    window.location.reload();
  }

  selectRange(type: string) {
    this.selectedRangeType = type;
    const range = this.dateRangeService.getDateRange(type);

    // Gunakan service untuk menyimpan dan menyebarkan perubahan
    this.dateRangeService.saveDateRange(range.start, range.end, type);

    // Perbarui tampilan kalender
    this.updateCalendarView();

    // Log untuk debugging
    console.log('Selected Predefined Range:', {
      type: type,
      startDate: {
        date: range.start.getDate(),
        month: range.start.getMonth() + 1,
        year: range.start.getFullYear(),
        fullDate: range.start.toLocaleDateString(),
      },
      endDate: {
        date: range.end.getDate(),
        month: range.end.getMonth() + 1,
        year: range.end.getFullYear(),
        fullDate: range.end.toLocaleDateString(),
      },
      numberOfDays: Math.floor(
        (range.end.getTime() - range.start.getTime())
        / (1000 * 60 * 60 * 24)
      ) + 1
    });

    // Refresh halaman setelah sedikit delay untuk memastikan data tersimpan
    setTimeout(() => {
      this.refreshPage();
    }, 300);
  }

  onDateClick(date: Date) {
    if (!this.isSelectingRange) {
      this.selectedRange.start = date;
      this.selectedRange.end = date;
      this.isSelectingRange = true;
    } else {
      if (date < this.selectedRange.start) {
        this.selectedRange.end = this.selectedRange.start;
        this.selectedRange.start = date;
      } else {
        this.selectedRange.end = date;
      }
      this.isSelectingRange = false;

      // Ketika memilih custom date, simpan sebagai tipe 'custom'
      this.selectedRangeType = 'custom';

      // Gunakan service untuk menyimpan dan menyebarkan perubahan
      this.dateRangeService.saveDateRange(
        this.selectedRange.start,
        this.selectedRange.end,
        'custom'
      );

      // Refresh halaman setelah sedikit delay untuk memastikan data tersimpan
      setTimeout(() => {
        this.refreshPage();
      }, 300);
    }
  }

  onDateHover(date: Date) {
    this.hoveredDate = date;
  }

  isInRange(date: Date): boolean {
    return date >= this.selectedRange.start && date <= (this.hoveredDate || this.selectedRange.end);
  }

  isSelected(date: Date): boolean {
    return this.dateRangeService.isSameDay(date, this.selectedRange.start) ||
      this.dateRangeService.isSameDay(date, this.selectedRange.end);
  }

  formatDateRange(): string {
    return `${this.formatDate(this.selectedRange.start)} - ${this.formatDate(this.selectedRange.end)}`;
  }

  isOtherMonth(date: Date, monthDate: Date): boolean {
    return date.getMonth() !== monthDate.getMonth();
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  }

  getCellClass(date: Date | null): string {
    if (!date) return 'date-cell disabled';

    let classes = ['date-cell'];

    if (this.isSelected(date)) {
      classes.push('selected');
    } else if (this.isInRange(date)) {
      classes.push('in-range');
    }

    return classes.join(' ');
  }
}
